import { PatternData } from "../types/types";

export const getLanguageAbbreviation = (language: string) => {
  const languageMap: { [key: string]: string } = {
    Portuguese: "PT",
    Spanish: "ES",
    English: "EN",
    Italian: "IT",
    French: "FR",
    German: "DE",
  };
  return languageMap[language] || language;
};

type LanguageMap = {
  [key: string]: string;
};

type LanguageMappings = {
  [key: string]: LanguageMap;
};

export const languageMappings: LanguageMappings = {
  en: {
    English: "English",
    Portuguese: "Portuguese",
    Spanish: "Spanish",
    German: "German",
    French: "French",
    Italian: "Italian",
  },
  pt: {
    English: "Inglês",
    Portuguese: "Português",
    Spanish: "Espanhol",
    German: "Alemão",
    French: "Francês",
    Italian: "Italiano",
  },
};

export const translateLanguages = (
  languages: string[],
  locale: "en" | "pt"
): string => {
  if (languages.length === 0) {
    return "";
  } else if (languages.length === 1) {
    return languageMappings[locale][languages[0]] || languages[0];
  }

  const languagesCopy = [...languages];
  const lastLanguage = languagesCopy.pop();

  const translatedLast = lastLanguage
    ? languageMappings[locale][lastLanguage]
    : "";
  const translatedOthers = languagesCopy.map(
    (language) => languageMappings[locale][language] || language
  );

  const conjunction = locale === "en" ? " and " : " e ";

  return `${translatedOthers.join(", ")}${conjunction}${translatedLast}`;
};

export function validateEmail(email: string) {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).toLowerCase());
}

export function sanitizeText(text: string) {
  return text.replace(/<\/?[^>]+(>|$)/g, "");
}

export const isValidCustomerID = (id: string) => {
  const cleanedID = id.trim();

  return (
    cleanedID.length >= 5 &&
    cleanedID.length <= 20 &&
    /^[a-zA-Z0-9./-]+$/.test(cleanedID)
  );
};

export function formatToCurrency(
  value: number,
  currency: "USD" | "BRL" = "USD"
): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatDate(dateString: string | number | Date): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export const convertTimestampToDate = (timestamp: number): string => {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const findPatternByName = (
  patterns: PatternData[],
  patternName: string
) => {
  const pattern = patterns.find((p) => p.patternName.en === patternName);
  return pattern ? pattern.id : null;
};

function getCommonElements<T>(arrays: T[][]): T[] {
  if (arrays.length === 0) return [];
  return arrays.reduce((common, array) => {
    return common.filter((element) => array.includes(element));
  });
}

export const getLanguagesForPack = (
  patterns: PatternData[],
  packPatternIds: string[]
): string[] => {
  const packPatterns = patterns.filter(
    (pattern) => pattern.id && packPatternIds.includes(pattern.id)
  );
  const allLanguages = packPatterns.map((pattern) =>
    pattern.files.map((file) => file.language)
  );
  return getCommonElements(allLanguages);
};

export const sendThankYouEmail = async (
  userName: string,
  userEmail: string,
  language: string
) => {
  try {
    const response = await fetch(
      `${process.env.GATSBY_CLOUD_FUNCTIONS_URL}/sendThankYouEmail` as string,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: userName,
          email: userEmail,
          language,
        }),
      }
    );

    if (!response.ok) {
      console.error("Failed to send email:", response.statusText);
    } else {
      console.log("Thank-you email sent successfully.");
    }
  } catch (error) {
    console.error("Error sending thank-you email:", error);
  }
};
